import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";

/** "minis" set in the display face with a hand-written "by Cass", like a signature on a price tag. */
export function Wordmark({ className = "", tone = "dark" }: { className?: string; tone?: "dark" | "light" }) {
  return (
    <Link to="/" aria-label="Minis by Cass, home" className={`group inline-flex items-baseline gap-1.5 leading-none ${className}`}>
      <span className={`font-display text-[1.75rem] tracking-[-.01em] ${tone === "dark" ? "text-cocoa" : "text-white"}`}>minis</span>
      <span className={`-translate-y-px font-hand text-xl transition-transform duration-300 group-hover:-rotate-6 ${tone === "dark" ? "text-ribbon-ink" : "text-blush"}`}>by Cass</span>
    </Link>
  );
}

/**
 * A technical-drawing dimension line with end ticks and a hand-written label,
 * echoing the measurement arrows Cass draws on her own product photos.
 */
export function DimensionLine({ label, orientation = "horizontal", className = "", tone = "ribbon" }: {
  label: ReactNode; orientation?: "horizontal" | "vertical"; className?: string; tone?: "ribbon" | "cocoa" | "light";
}) {
  const color = tone === "ribbon" ? "text-ribbon-ink" : tone === "cocoa" ? "text-cocoa/70" : "text-white/80";
  if (orientation === "vertical") {
    return (
      <span className={`relative flex flex-col items-center ${color} ${className}`} aria-hidden="true">
        <span className="h-px w-3 bg-current" />
        <span className="w-px flex-1 bg-current" />
        <span className="my-2 whitespace-nowrap font-hand text-lg leading-none [writing-mode:vertical-rl] rotate-180">{label}</span>
        <span className="w-px flex-1 bg-current" />
        <span className="h-px w-3 bg-current" />
      </span>
    );
  }
  return (
    <span className={`flex items-center ${color} ${className}`} aria-hidden="true">
      <span className="h-3 w-px bg-current" />
      <span className="h-px flex-1 bg-current" />
      <span className="mx-2 whitespace-nowrap font-hand text-lg leading-none">{label}</span>
      <span className="h-px flex-1 bg-current" />
      <span className="h-3 w-px bg-current" />
    </span>
  );
}

const stickerTones = {
  butter: "bg-butter text-cocoa",
  mint: "bg-mint-soft text-mint",
  blush: "bg-blush text-ribbon-ink",
  cocoa: "bg-cocoa text-white",
  white: "bg-white text-cocoa ring-1 ring-line"
};

/** A small die-cut label, slightly askew, as if pressed on by hand. */
export function Sticker({ children, tone = "butter", tilt = -3, className = "", style }: {
  children: ReactNode; tone?: keyof typeof stickerTones; tilt?: number; className?: string; style?: CSSProperties;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-[4px] px-2 py-0.5 text-[12px] font-bold leading-5 shadow-sticker ${stickerTones[tone]} ${className}`}
      style={{ transform: `rotate(${tilt}deg)`, ...style }}
    >
      {children}
    </span>
  );
}

/** The four real stages of an order. Used on cart, checkout, payment and confirmation. */
export function CheckoutSteps({ current }: { current: 0 | 1 | 2 | 3 }) {
  const steps = ["Bag", "Details", "Payment", "Done"];
  return (
    <ol className="flex items-center gap-2 text-[13px] font-bold" aria-label="Checkout progress">
      {steps.map((step, index) => (
        <li key={step} className="flex items-center gap-2" aria-current={index === current ? "step" : undefined}>
          <span className={`grid size-6 place-items-center rounded-full text-[11px] ${index < current ? "bg-cocoa text-white" : index === current ? "bg-ribbon text-white" : "border border-line text-muted"}`}>{index + 1}</span>
          <span className={index === current ? "text-cocoa" : "hidden text-muted sm:inline"}>{step}</span>
          {index < steps.length - 1 && <span className={`h-px w-5 sm:w-8 ${index < current ? "bg-cocoa" : "bg-line"}`} />}
        </li>
      ))}
    </ol>
  );
}
